import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import {
    Body1,
    Button,
    Card,
    Image,
    makeStyles,
    mergeClasses,
    shorthands,
    Spinner,
    Text,
    Title3,
    tokens,
    Tree,
    TreeItem,
    TreeItemLayout,
} from '@fluentui/react-components'
import { bundleIcon, PersonSwapFilled, PersonSwapRegular } from '@fluentui/react-icons'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from '../../context/AuthorizationContext'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingVerticalM,
    },
    containerXXL: {
        width: '70%',
        alignSelf: 'center',
    },
    logo: {
        maxHeight: '100px',
    },
    user: {
        display: 'flex',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    username: {
        flexGrow: 1,
        flexShrink: 1,
        overflowWrap: 'break-word',
        ...shorthands.padding(0, tokens.spacingHorizontalM),
    },
    vertical: {
        flexDirection: 'column',
    },
    hosts: {
        '> :nth-child(even)': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
    host: {
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
})

const PersonSwap = bundleIcon(PersonSwapFilled, PersonSwapRegular)

export default function SelectHost(): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { mailbox, user, hosts, host, setHost, reset } = useContext(AuthorizationContext)

    const containerXXL = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])

    return (
        <div className={mergeClasses(styles.container, containerXXL && styles.containerXXL)}>
            <Image src={'images/hammock.svg'} className={styles.logo} />

            {!!host && <Spinner tabIndex={0} size={'small'} label={translate('authentication.host.redirect')} />}

            {!host && (
                <>
                    <Title3>{translate('authentication.host.select.title')}</Title3>
                    {!!user && (
                        <div className={mergeClasses(styles.user, isViewport('<=', Viewport.MainMenu) && styles.vertical)}>
                            <Body1 className={styles.username}>
                                {translate('authentication.host.select.subtitle')} <Text italic>{user.name || translate('base.unknown')}.</Text>
                                {user.username !== mailbox && (
                                    <>
                                        {translate('authentication.host.select.subtitle2')}{' '}
                                        <Text italic>{mailbox || translate('base.unknown')}.</Text>
                                    </>
                                )}
                            </Body1>
                            <div>
                                <Button appearance="subtle" icon={<PersonSwap />} onClick={() => reset()}>
                                    {translate('authentication.host.select.account.change')}
                                </Button>
                            </div>
                        </div>
                    )}
                    <Card>
                        <Tree className={styles.hosts} aria-label={'omgevingen'}>
                            {(hosts || [])
                                .sort((a, b) => a.Name.localeCompare(b.Name))
                                .map(host => (
                                    <TreeItem key={host.RowKey} itemType="leaf" onClick={() => setHost(host)}>
                                        <TreeItemLayout className={styles.host}>{host.Name}</TreeItemLayout>
                                    </TreeItem>
                                ))}
                            {!hosts && (
                                <TreeItem key={'-1'} itemType="leaf">
                                    <TreeItemLayout>
                                        <Spinner size="extra-tiny" label={translate('base.loading')} />
                                    </TreeItemLayout>
                                </TreeItem>
                            )}
                        </Tree>
                    </Card>
                </>
            )}
        </div>
    )
}
